import React, { useState, useEffect } from "react";
import { Save, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Node } from "../../../../pages/journey/types";

interface PropertiesPanelProps {
  selectedNode: Node | null;
  onUpdateNode: (nodeId: string, data: any) => void;
  onDeleteNode: (nodeId: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedNode,
  onUpdateNode,
  onDeleteNode,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [formData, setFormData] = useState<any>({});

  useEffect(() => {
    if (selectedNode) {
      setFormData(selectedNode.data || {});
    }
  }, [selectedNode]);

  if (isCollapsed) {
    return (
      <div className="w-12 bg-gray-50 border-l border-gray-200 p-2 flex flex-col items-center">
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-gray-100 rounded mb-4"
          title="Expand properties"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        {selectedNode && (
          <div className="text-center">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mb-2">
              <span className="text-xs text-white font-bold">
                {selectedNode.type.charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="text-xs text-gray-600 truncate block">
              {selectedNode.label}
            </span>
          </div>
        )}
      </div>
    );
  }

  if (!selectedNode) {
    return (
      <div className="w-80 bg-gray-50 border-l border-gray-200 p-4 flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700">Properties</h3>
          <button
            onClick={onToggleCollapse}
            className="p-1 hover:bg-gray-100 rounded"
            title="Collapse properties"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-gray-500 text-center">
            Select a node to configure it.
          </p>
        </div>
      </div>
    );
  }

  const handleInputChange = (key: string, value: any) => {
    const newData = { ...formData, [key]: value };
    setFormData(newData);
    onUpdateNode(selectedNode.id, newData);
  };

  const handleSaveConfiguration = () => {
    toast.success("Configuration saved successfully!");
  };

  const renderWhatsAppConfig = () => (
    <div className="space-y-6">
      {/* Parameters Section */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Parameters</h4>
        
        {/* Settings */}
        <div className="space-y-4">
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-3">Settings</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Credential to connect with
                </label>
                <select
                  value={formData.credential || ""}
                  onChange={(e) => handleInputChange("credential", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Credential</option>
                  <option value="whatsapp-business">WhatsApp Business API</option>
                  <option value="twilio">Twilio</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Resource</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Message</label>
                <select
                  value={formData.resource || "message"}
                  onChange={(e) => handleInputChange("resource", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="message">Message</option>
                  <option value="template">Template</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Operation</label>
                <select
                  value={formData.operation || "send"}
                  onChange={(e) => handleInputChange("operation", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="send">Send</option>
                  <option value="send-wait">Send and Wait for Response</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Send Message To</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">User</label>
                <select
                  value={formData.sendTo || "user"}
                  onChange={(e) => handleInputChange("sendTo", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="user">User</option>
                  <option value="group">Group</option>
                  <option value="list">From list</option>
                </select>
              </div>
              {formData.sendTo === "list" && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Select a user...</label>
                  <select
                    value={formData.userList || ""}
                    onChange={(e) => handleInputChange("userList", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a user...</option>
                    <option value="user1">User 1</option>
                    <option value="user2">User 2</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Message</h5>
            <textarea
              value={formData.message || ""}
              onChange={(e) => handleInputChange("message", e.target.value)}
              rows={6}
              placeholder="Type your message here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTriggerConfig = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Trigger Settings</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Trigger Type</label>
            <select
              value={formData.triggerType || "schedule"}
              onChange={(e) => handleInputChange("triggerType", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="schedule">Schedule</option>
              <option value="webhook">Webhook</option>
              <option value="manual">Manual</option>
              <option value="event">Event</option>
            </select>
          </div>

          {formData.triggerType === "schedule" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type</label>
              <select
                value={formData.scheduleType || "cron"}
                onChange={(e) => handleInputChange("scheduleType", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="cron">Cron Expression</option>
                <option value="interval">Interval</option>
                <option value="specific">Specific Time</option>
              </select>
              
              {formData.scheduleType === "cron" && (
                <div className="mt-2">
                  <label className="block text-sm text-gray-600 mb-1">Cron Expression</label>
                  <input
                    type="text"
                    value={formData.cron || "0 0 * * *"}
                    onChange={(e) => handleInputChange("cron", e.target.value)}
                    placeholder="0 0 * * *"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              )}
            </div>
          )}

          {formData.triggerType === "webhook" && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
                <input
                  type="text"
                  value={formData.webhookUrl || ""}
                  onChange={(e) => handleInputChange("webhookUrl", e.target.value)}
                  placeholder="https://api.example.com/webhook"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">HTTP Method</label>
                <select
                  value={formData.httpMethod || "POST"}
                  onChange={(e) => handleInputChange("httpMethod", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                  <option value="PUT">PUT</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderDelayConfig = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Delay Settings</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Delay Type</label>
            <select
              value={formData.delayType || "fixed"}
              onChange={(e) => handleInputChange("delayType", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="fixed">Fixed Duration</option>
              <option value="until">Until Specific Time</option>
              <option value="random">Random Range</option>
            </select>
          </div>

          {formData.delayType === "fixed" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                <input
                  type="number"
                  value={formData.duration || 5}
                  onChange={(e) => handleInputChange("duration", parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                <select
                  value={formData.unit || "minutes"}
                  onChange={(e) => handleInputChange("unit", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                >
                  <option value="seconds">Seconds</option>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderConfigByType = () => {
    switch (selectedNode.type) {
      case "trigger":
        return renderTriggerConfig();
      case "delay":
        return renderDelayConfig();
      case "whatsapp":
        return renderWhatsAppConfig();
      case "sms":
      case "email":
      case "ai_call":
      case "rcs":
        return renderWhatsAppConfig(); // Use similar structure for other message types
      default:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
              <input
                type="text"
                value={formData.label || ""}
                onChange={(e) => handleInputChange("label", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="w-80 bg-gray-50 border-l border-gray-200 flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700">
          {selectedNode.label} Properties
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleSaveConfiguration}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Save Configuration"
          >
            <Save className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={onToggleCollapse}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Collapse Properties"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {renderConfigByType()}
      </div>
      
      <div className="p-4 border-t border-gray-200">
        {selectedNode.type !== "trigger" && (
          <button
            onClick={() => {
              if (window.confirm("Are you sure you want to delete this node?")) {
                onDeleteNode(selectedNode.id);
              }
            }}
            className="flex items-center space-x-2 w-full px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete Node</span>
          </button>
        )}
      </div>
    </div>
  );
};